import { computed, ref, watch, type Ref } from 'vue';
import { clampNumber } from '../lib/color-sanitizer';
import { useFormBuilderStore } from './form-builder.store';
import { TableElement, TableProperties } from './form.types';
import { useTableEditorStore } from './table-editor.store';
import type { BorderPattern } from '../lib/cell-border-utils';
import { TABLE_BORDER_DEFAULTS } from '../lib/table-constants';

type TableBorderStyle = NonNullable<TableProperties['borderStyle']>;

export interface TablePropertyUpdates {
  name?: string;
  borderWidth?: number;
  tableWidth?: number;
  is_multiple?: boolean;
  borderColor?: string;
  borderStyle?: TableBorderStyle;
  borderPattern?: BorderPattern;
  padding?: TableProperties['padding'];
  margin?: TableProperties['margin'];
}

const TABLE_WIDTH_MIN = 1;
const TABLE_WIDTH_MAX = 100;
const BORDER_WIDTH_MIN = 1;
const BORDER_WIDTH_MAX = 5;
const DEFAULT_TABLE_WIDTH = 100;
const DEFAULT_BORDER_WIDTH = TABLE_BORDER_DEFAULTS.WIDTH;
const DEFAULT_BORDER_COLOR = TABLE_BORDER_DEFAULTS.COLOR;
const DEFAULT_BORDER_STYLE: TableBorderStyle = TABLE_BORDER_DEFAULTS.STYLE;
const DEFAULT_BORDER_PATTERN: BorderPattern = TABLE_BORDER_DEFAULTS.PATTERN;
const NAME_MAX_LENGTH = 100;

function validateName(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error('O nome é obrigatório');
  }
  if (trimmed.length > NAME_MAX_LENGTH) {
    throw new Error(`Máximo de ${NAME_MAX_LENGTH} caracteres`);
  }
  return trimmed;
}

/**
 * Validates and sanitizes table width value
 */
function validateTableWidth(value: number): number {
  return clampNumber(value, TABLE_WIDTH_MIN, TABLE_WIDTH_MAX);
}

/**
 * Validates and sanitizes border width value
 */
function validateBorderWidth(value: number): number {
  return clampNumber(value, BORDER_WIDTH_MIN, BORDER_WIDTH_MAX);
}

/**
 * Sanitizes numeric input string (removes non-digits, limits length)
 */
function sanitizeNumericInput(input: string, maxLength: number = 3): string {
  return input.replace(/\D/g, '').slice(0, maxLength);
}

/**
 * Parses numeric string to number with validation
 */
function parseNumericInput(input: string): number | null {
  const parsed = Number.parseInt(input, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Composable for managing table properties with business logic extracted to model layer
 *
 * @param elementId - The ID of the table element
 * @returns Reactive state and methods for table property management
 */
export function useTableProperties(elementId: string | Ref<string>) {
  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();

  const elementIdRef = typeof elementId === 'string' ? ref(elementId) : elementId;

  // Get table element
  const element = computed<TableElement | undefined>(() => {
    const id = elementIdRef.value;
    if (!id) return undefined;

    const el = formBuilderStore.elements.find((e) => e.id === id);
    return el?.type === 'TABLE' ? (el as TableElement) : undefined;
  });

  // Get table properties with defaults
  const properties = computed<TableProperties>(() => {
    if (!element.value?.properties) {
      return {
        name: '',
        rows: 3,
        columns: 3,
        cells: [],
        borderWidth: DEFAULT_BORDER_WIDTH,
        tableWidth: DEFAULT_TABLE_WIDTH,
        borderColor: DEFAULT_BORDER_COLOR,
        borderStyle: DEFAULT_BORDER_STYLE,
        borderPattern: DEFAULT_BORDER_PATTERN,
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        margin: { top: 0, right: 0, bottom: 0, left: 0 },
      };
    }
    // Ensure name is set if missing
    const props = element.value.properties;
    if (!props.name || props.name.trim() === '') {
      const shortId = element.value.id.replace(/^(table|header-table)-/, '').slice(0, 8);
      const generatedName = element.value.id.startsWith('header-table-')
        ? `header_table_${shortId}`
        : `table_${shortId}`;
      // Return properties with generated name
      return {
        ...props,
        name: generatedName,
      };
    }
    return props;
  });

  // Auto-generate name if missing when element is created or loaded
  watch(
    () => element.value,
    (newElement) => {
      if (newElement?.type === 'TABLE') {
        const currentName = newElement.properties?.name;
        if (!currentName || currentName.trim() === '') {
          const shortId = newElement.id.replace(/^(table|header-table)-/, '').slice(0, 8);
          const generatedName = newElement.id.startsWith('header-table-')
            ? `header_table_${shortId}`
            : `table_${shortId}`;
          // Update synchronously to ensure it's set immediately
          updateProperties({ name: generatedName });
        }
      }
    },
    { immediate: true }
  );

  const name = computed(() => {
    const storedName = element.value?.properties?.name;
    if (storedName && storedName.trim() !== '') {
      return storedName;
    }

    const id = element.value?.id ?? elementIdRef.value;
    if (id) {
      const shortId = id.replace(/^(table|header-table)-/, '').slice(0, 8);
      return id.startsWith('header-table-') ? `header_table_${shortId}` : `table_${shortId}`;
    }

    return '';
  });
  const borderWidth = computed(() => properties.value.borderWidth ?? DEFAULT_BORDER_WIDTH);
  const tableWidth = computed(() => properties.value.tableWidth ?? DEFAULT_TABLE_WIDTH);
  const borderColor = computed(() => properties.value.borderColor ?? DEFAULT_BORDER_COLOR);
  const borderStyle = computed(() => properties.value.borderStyle ?? DEFAULT_BORDER_STYLE);
  const borderPattern = computed(() => properties.value.borderPattern ?? DEFAULT_BORDER_PATTERN);
  const padding = computed(
    () => properties.value.padding ?? { top: 0, right: 0, bottom: 0, left: 0 }
  );
  const margin = computed(
    () => properties.value.margin ?? { top: 0, right: 0, bottom: 0, left: 0 }
  );
  const rows = computed(() => properties.value.rows ?? 3);
  const columns = computed(() => properties.value.columns ?? 3);

  const tableWidthInput = ref<string>(String(tableWidth.value));

  watch(
    () => element.value?.id,
    (newId) => {
      if (newId) {
        const width = properties.value.tableWidth ?? DEFAULT_TABLE_WIDTH;
        tableWidthInput.value = String(width);
      }
    },
    { immediate: true }
  );

  /**
   * Updates table properties with validation and sanitization
   */
  function updateProperties(updates: TablePropertyUpdates): void {
    if (!element.value) return;

    const validatedUpdates: Partial<TableProperties> = {};

    if (updates.name !== undefined) {
      validatedUpdates.name = validateName(updates.name);
    }

    if (updates.borderWidth !== undefined) {
      validatedUpdates.borderWidth = validateBorderWidth(updates.borderWidth);
    }

    if (updates.tableWidth !== undefined) {
      validatedUpdates.tableWidth = validateTableWidth(updates.tableWidth);
    }

    if (updates.is_multiple !== undefined) {
      validatedUpdates.is_multiple = updates.is_multiple;
    }

    if (updates.borderColor !== undefined) {
      validatedUpdates.borderColor = updates.borderColor;
    }

    if (updates.borderStyle !== undefined) {
      validatedUpdates.borderStyle = updates.borderStyle;
    }

    if (updates.borderPattern !== undefined) {
      validatedUpdates.borderPattern = updates.borderPattern;
    }

    if (updates.padding !== undefined) {
      validatedUpdates.padding = updates.padding;
    }

    if (updates.margin !== undefined) {
      validatedUpdates.margin = updates.margin;
    }

    formBuilderStore.updateElement(element.value.id, {
      properties: {
        ...element.value.properties,
        ...validatedUpdates,
      },
    });
  }

  /**
   * Updates border width
   */
  function setBorderWidth(value: number): void {
    updateProperties({ borderWidth: value });
  }

  /**
   * Updates table width
   */
  function setTableWidth(value: number): void {
    const validated = validateTableWidth(value);
    updateProperties({ tableWidth: validated });
    tableWidthInput.value = String(validated);
  }

  /**
   * Handles table width input change (sanitizes input)
   */
  function handleTableWidthInput(input: string): void {
    tableWidthInput.value = sanitizeNumericInput(input, 3);
  }

  /**
   * Handles table width input blur (validates and commits value)
   */
  function handleTableWidthBlur(): void {
    if (!element.value) return;

    const parsed = parseNumericInput(tableWidthInput.value);

    if (parsed !== null && parsed > 0) {
      const validated = validateTableWidth(parsed);
      setTableWidth(validated);
    } else {
      // Reset to current value if invalid
      const currentWidth = tableWidth.value;
      tableWidthInput.value = String(currentWidth);
    }
  }

  /**
   * Table structure operations
   */
  function addRow(): void {
    tableEditorStore.addRow();
  }

  function removeRow(): void {
    if (rows.value <= 1) return;
    tableEditorStore.removeRow();
  }

  function addColumn(): void {
    tableEditorStore.addColumn();
  }

  function removeColumn(): void {
    if (columns.value <= 1) return;
    tableEditorStore.removeColumn();
  }

  // Computed for slider (array format)
  const borderWidthArray = computed({
    get: () => [borderWidth.value],
    set: (value: number[]) => {
      if (value.length > 0) {
        setBorderWidth(value[0]);
      }
    },
  });

  const tableWidthArray = computed({
    get: () => [tableWidth.value],
    set: (value: number[]) => {
      if (value.length > 0) {
        setTableWidth(value[0]);
      }
    },
  });

  return {
    element,
    properties,
    name,
    borderWidth,
    tableWidth,
    borderColor,
    borderStyle,
    borderPattern,
    padding,
    margin,
    rows,
    columns,
    tableWidthInput,
    borderWidthArray,
    tableWidthArray,
    updateProperties,
    setBorderWidth,
    setTableWidth,
    handleTableWidthInput,
    handleTableWidthBlur,
    addRow,
    removeRow,
    addColumn,
    removeColumn,
  };
}
